import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { applyTheme, getThemeId } from './utils/themes'

// Apply persisted theme before first paint to avoid a flash of default colors
applyTheme(getThemeId())

// Fire-and-forget boot wake: Render free-tier dynos sleep after ~15min of
// idle. Cold-start 503s come from Render infra with no CORS headers, which
// the browser blocks BEFORE the FastAPI app can respond. By pinging /health
// at app boot we trigger the dyno spin-up so it's warm by the time the user
// clicks anything. Uses no-cors so a sleeping server doesn't even cause a
// network error in the console.
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '');
if (API_BASE) {
  const pingHealth = () => {
    fetch(`${API_BASE}/health`, { method: 'GET', mode: 'no-cors', cache: 'no-store' }).catch(() => {});
  };
  // Defer one tick so the main bundle execution isn't held up
  setTimeout(pingHealth, 0);
  // Keep-alive: Render free-tier sleeps after ~15min idle. A 12-minute ping
  // keeps the dyno warm during a session so /attempts POSTs don't surprise
  // the user with a cold-start CORS error after a long practice run. The
  // ping only fires when the tab is visible — background tabs let it sleep.
  setInterval(() => {
    if (document.visibilityState === 'visible') pingHealth();
  }, 12 * 60 * 1000);
}

// Prefetch the two most-likely next chunks once the browser is idle —
// when the user logs in (or the dashboard mounts on returning users),
// the Dashboard + Practice chunks are already in the HTTP cache so the
// transition feels instant. Falls back to a 2s timer when
// requestIdleCallback isn't available (Safari < 16.4).
const prefetchNextChunks = () => {
  void import('./components/dashboard/Dashboard');
  void import('./components/practice/PracticeContainer');
};
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback?.(prefetchNextChunks);
  } else {
    setTimeout(prefetchNextChunks, 2000);
  }
}

// Recovery from stale-deploy chunk 404s: if a dynamic import fails (browser
// holds a stale index.html that references chunks no longer on the CDN),
// reload exactly once with cache bypass. The flag prevents infinite loops
// when the failure is for some other reason.
const RELOAD_FLAG = 'bagrut_chunk_reload';
const RELOAD_COOLDOWN_MS = 60_000; // allow a new recovery every 60s

/** Decide whether we should attempt a recovery now. Reads a timestamp so a
 *  second stale-chunk hit later in the same session (e.g. user navigates to
 *  another lazy route after the first deploy bump) can still recover.
 *  Returns true if we should proceed; also marks the recovery attempt. */
function shouldRecover(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_FLAG);
    const last = raw ? parseInt(raw, 10) : 0;
    if (Number.isFinite(last) && last > 0 && Date.now() - last < RELOAD_COOLDOWN_MS) {
      return false;
    }
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    return true;
  } catch {
    return true; // sessionStorage unavailable — still try
  }
}

function tryRecoverFromChunkError(event: ErrorEvent | PromiseRejectionEvent) {
  const reason = (event as PromiseRejectionEvent).reason ?? (event as ErrorEvent).error;
  const msg = (reason && (reason.message || String(reason))) || '';
  const isChunkError = /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(msg);
  if (!isChunkError) return;
  if (!shouldRecover()) return;
  console.warn('[Recovery] Stale chunk detected — reloading to refresh bundle');
  // Best-effort SW unregister so we don't loop on a cached index.html
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister());
      window.location.reload();
    }).catch(() => window.location.reload());
  } else {
    window.location.reload();
  }
}
window.addEventListener('unhandledrejection', tryRecoverFromChunkError);
window.addEventListener('error', tryRecoverFromChunkError);

// In dev mode, unregister any stale service workers that interfere with Vite
if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    for (const reg of regs) {
      reg.unregister();
      console.log('[Dev] Unregistered stale service worker:', reg.scope);
    }
  });
}

// Suppress noisy dev-only console messages
if (import.meta.env.DEV) {
  const suppress = [
    'Download the React DevTools',
    'React DevTools',
    'apple-mobile-web-app-capable',
    'mobile-web-app-capable',
  ];
  for (const method of ['log', 'warn', 'info', 'error'] as const) {
    const orig = console[method];
    console[method] = (...args: unknown[]) => {
      const msg = args[0]?.toString() || '';
      if (suppress.some(s => msg.includes(s))) return;
      orig.apply(console, args);
    };
  }
}

// Silence KaTeX's "No character metrics for 'X'" warnings.
// They fire for every Hebrew character inside \text{...} blocks because
// KaTeX's bundled Computer-Modern fonts don't ship Hebrew metrics. The
// characters still render (browser falls back to the surrounding font),
// so the warning is purely noise — and on a question-rich screen there
// can be hundreds of them, drowning out real warnings.
{
  const originalWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    const first = args[0]?.toString() || '';
    if (first.startsWith('No character metrics for')) return;
    originalWarn.apply(console, args);
  };
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
